/**
 * Plot Card Dialog Component
 *
 * This component provides a dialog for creating and editing plot cards in the timeline.
 * It includes fields for title, content, type selection, and tags (for existing cards).
 */

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// UI Components
import { TagSelector } from "@/components/tags/tag-selector";
import { Button } from "@/components/ui/button";
import { DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { Textarea } from "@/components/ui/textarea";

// Types
import { CardType } from "../types";
import { BookOpen, Calendar, FileText, Layers, Tag as TagIcon, Type } from "lucide-react";



/**
 * Props for the AddPlotCardDialogContent component
 */
interface AddPlotCardDialogContentProps {
    /** Callback function when the form is saved */
    onSave: (data: { title: string; content: string; type: CardType; plotLineId: number; chapterId?: number }) => void;
    /** Callback function when the dialog is closed */
    onClose: () => void;
    /** Payload data passed by the dialog provider */
    data?: {
        /** Initial data for editing an existing card */
        initialData?: {
            title: string;
            content: string;
            type: CardType;
            id?: number;
            plotLineId?: number;
            chapterId?: number;
        };
        /** Available plot lines to select from */
        plotLines?: { id: number; title: string }[];
        /** Provide plotLineId if opening from a specific timeline context */
        plotLineId?: number;
        /** Provide chapterId if opening from a specific chapter context */
        chapterId?: number;
        /** Default chapter Name to display */
        defaultChapterName?: string;
    };
}

/**
 * Available card types for the plot card
 */
const allCardTypes: { value: CardType; label: string }[] = [
    { value: "plot", label: "Plot Point" },
    { value: "character", label: "Character Arc" },
    { value: "setting", label: "Setting" },
    { value: "conflict", label: "Conflict" },
    { value: "theme", label: "Theme" },
    { value: "note", label: "Note" },
    { value: "ai-context-prompt", label: "AI Context Prompt" }
];

/**
 * Dialog content for adding or editing a plot card
 */
export const AddPlotCardDialogContent: React.FC<AddPlotCardDialogContentProps> = ({
    onSave,
    onClose,
    data
}) => {
    // Destructure data safely
    const {
        initialData,
        plotLines = [],
        plotLineId,
        chapterId,
        defaultChapterName
    } = data || {};

    // Get the current project ID from the URL
    const { projectId } = useParams();

    // Filter card types (AI features disabled)
    const cardTypes = allCardTypes.filter(cardType => {
        if (cardType.value === 'ai-context-prompt') {
            return false; // AI features disabled
        }
        return true;
    });

    // Form state
    const [title, setTitle] = useState(initialData?.title || "");
    const [content, setContent] = useState(initialData?.content || "");
    const [type, setType] = useState<CardType>(initialData?.type || "plot");
    const [selectedPlotLineId, setSelectedPlotLineId] = useState<string>(
        initialData?.plotLineId?.toString() || plotLineId?.toString() || (plotLines.length > 0 ? plotLines[0].id.toString() : "")
    );
    const [selectedChapterId] = useState<number | undefined>(initialData?.chapterId || chapterId);

    // Initial check for plot lines
    useEffect(() => {
        if (!selectedPlotLineId && plotLines.length > 0) {
            setSelectedPlotLineId(plotLines[0].id.toString());
        }
    }, [plotLines, selectedPlotLineId]);

    /**
     * Handle form submission
     * Saves the card data and closes the dialog
     */
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave({
            title,
            content,
            type,
            plotLineId: Number(selectedPlotLineId),
            chapterId: selectedChapterId
        });
        onClose();
    };

    return (
        <DialogContent className="sm:max-w-[650px] sm:rounded-2xl border-border/60 shadow-2xl p-0 gap-0 overflow-hidden bg-background/95 backdrop-blur-xl">
            {/* Dialog header with title and description */}
            <DialogHeader className="px-8 py-6 border-b border-border/40 bg-muted/5 space-y-1">
                <DialogTitle className="text-xl font-semibold tracking-tight text-foreground flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-primary" />
                    {initialData ? "Edit Plot Card" : "Add Plot Card"}
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground/80 leading-relaxed">
                    {initialData
                        ? "Edit the details of your plot card."
                        : defaultChapterName
                            ? `Create a new plot card for ${defaultChapterName}.`
                            : "Create a new plot card to add to your timeline."}
                </DialogDescription>
            </DialogHeader>

            {/* Plot card form */}
            <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 p-8">

                    {/* Left Column */}
                    <div className="space-y-5">
                        {/* Title field */}
                        <div className="space-y-2">
                            <Label htmlFor="title" className="text-sm font-medium text-foreground/80 flex items-center gap-1.5">
                                <Type className="w-3.5 h-3.5" /> Title
                            </Label>
                            <Input
                                id="title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="h-10 bg-muted/30 border-muted-foreground/10 focus:border-primary/30 focus:ring-primary/20 rounded-xl shadow-sm transition-all"
                                placeholder="Enter card title"
                                required
                                autoFocus
                            />
                        </div>

                        {/* Card type selection */}
                        <div className="space-y-2">
                            <Label htmlFor="type" className="text-sm font-medium text-foreground/80 flex items-center gap-1.5">
                                <Layers className="w-3.5 h-3.5" /> Type
                            </Label>
                            <Select value={type} onValueChange={(value: CardType) => setType(value)}>
                                <SelectTrigger className="h-10 bg-muted/30 border-muted-foreground/10 focus:border-primary/30 focus:ring-primary/20 rounded-xl shadow-sm transition-all">
                                    <SelectValue placeholder="Select a card type" />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl border-border/60 shadow-xl">
                                    {cardTypes.map((cardType) => (
                                        <SelectItem key={cardType.value} value={cardType.value} className="rounded-lg cursor-pointer my-0.5">
                                            {cardType.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Timeline Selection */}
                        <div className="space-y-2">
                            <Label htmlFor="timeline" className="text-sm font-medium text-foreground/80 flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5" /> Timeline
                            </Label>
                            {/* If plotLineId is provided via props or initialData or we have a selected plot line, display as a badge, otherwise show select */}
                            {plotLineId || (initialData?.plotLineId && plotLines.length === 0) ? (
                                <div className="px-3 py-2.5 bg-primary/5 border border-primary/10 rounded-xl text-sm font-medium text-foreground shadow-sm">
                                    {plotLines.find(p => p.id.toString() === selectedPlotLineId)?.title || "Selected Timeline"}
                                </div>
                            ) : plotLines.length > 0 ? (
                                <Select
                                    value={selectedPlotLineId}
                                    onValueChange={setSelectedPlotLineId}
                                    required
                                >
                                    <SelectTrigger className="h-10 bg-muted/30 border-muted-foreground/10 focus:border-primary/30 focus:ring-primary/20 rounded-xl shadow-sm transition-all">
                                        <SelectValue placeholder="Select a timeline" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl border-border/60 shadow-xl">
                                        {plotLines.map((line) => (
                                            <SelectItem key={line.id} value={line.id.toString()} className="rounded-lg cursor-pointer my-0.5">
                                                {line.title}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <div className="text-sm text-muted-foreground italic border border-dashed border-border/50 rounded-lg p-2.5 bg-muted/20">
                                    No timelines available. Please create one first.
                                </div>
                            )}
                        </div>
                        {/* Display Chapter if default is provided */}
                        {defaultChapterName && (
                            <div className="space-y-2">
                                <Label className="text-sm font-medium text-foreground/80 flex items-center gap-1.5">
                                    <Layers className="w-3.5 h-3.5" /> Chapter
                                </Label>
                                <div className="px-3 py-2.5 bg-primary/5 border border-primary/10 rounded-xl text-sm font-medium text-foreground shadow-sm">
                                    {defaultChapterName}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column */}
                    <div className="space-y-5 h-full flex flex-col">
                        {/* Content textarea */}
                        <div className="space-y-2 flex-1 flex flex-col">
                            <Label htmlFor="content" className="text-sm font-medium text-foreground/80 flex items-center gap-1.5">
                                <FileText className="w-3.5 h-3.5" /> Content
                            </Label>
                            <Textarea
                                id="content"
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                className="flex-1 min-h-[160px] bg-muted/30 border-muted-foreground/10 focus:border-primary/30 focus:ring-primary/20 rounded-xl shadow-sm resize-none transition-all leading-relaxed"
                                placeholder="Enter card description or content..."
                                required
                            />
                        </div>

                        {/* Tag selector - only shown when editing an existing card */}
                        {initialData?.id && projectId && (
                            <div className="space-y-2">
                                <Label className="text-sm font-medium text-foreground/80 flex items-center gap-1.5">
                                    <TagIcon className="w-3.5 h-3.5" /> Tags
                                </Label>
                                <div className="">
                                    <TagSelector
                                        projectId={projectId}
                                        plotcardId={initialData.id}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Dialog footer with action buttons */}
                <DialogFooter className="px-8 py-5 border-t border-border/40 bg-muted/5 gap-3">
                    <Button type="button" variant="ghost" onClick={onClose} className="rounded-xl hover:bg-muted/50 text-muted-foreground hover:text-foreground">
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={!selectedPlotLineId || plotLines.length === 0}
                        className="rounded-xl shadow-lg shadow-primary/20 px-6 font-medium"
                    >
                        Save Card
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
};

export default AddPlotCardDialogContent;